// Client side zip helpers for the Photos page "Download all" action.
// Photos are packed in store mode (JPEGs do not compress) into parts of at
// most PART_BUDGET_BYTES so one part never exhausts memory on a tablet.
import { Zip, ZipPassThrough } from 'fflate'

export const PART_BUDGET_BYTES = 200 * 1024 * 1024
// Used for the pre-download estimate when a photo's size is not known yet.
export const ASSUMED_PHOTO_BYTES = 4 * 1024 * 1024

// 'YYYY-MM-DD_HH-mm-ss_<id>.jpg' from the timestamp string the photos API
// returns ('2026-08-15T14:05:33.000Z'). Matches the time shown in the grid.
export function photoFileName(photo) {
  const ts = String(photo.timestamp || '')
  const day = ts.slice(0, 10) || 'photo'
  const clock = ts.includes('T') ? ts.slice(11, 19).replace(/:/g, '-') : '00-00-00'
  return `${day}_${clock}_${photo.id}.jpg`
}

// True when the next file must open a new part. A part always takes at least
// one file, so a single oversized photo still ships (in a part of its own).
export function startsNewPart(partBytes, partCount, nextBytes, budget = PART_BUDGET_BYTES) {
  return partCount > 0 && partBytes + nextBytes > budget
}

// Number of parts the given byte sizes would need. null or undefined sizes
// count as ASSUMED_PHOTO_BYTES. Returns 0 for an empty list.
export function estimatePartCount(sizes, budget = PART_BUDGET_BYTES) {
  let parts = 0
  let partBytes = 0
  let partCount = 0
  for (const raw of sizes) {
    const size = Number.isFinite(raw) && raw > 0 ? raw : ASSUMED_PHOTO_BYTES
    if (parts === 0 || startsNewPart(partBytes, partCount, size, budget)) {
      parts += 1
      partBytes = 0
      partCount = 0
    }
    partBytes += size
    partCount += 1
  }
  return parts
}

// 'photos-YYYY-MM-DD.zip' for a single part, 'photos-YYYY-MM-DD-partN.zip'
// when the day is split. partIndex is 1 based.
export function partFileName(day, partIndex, multiPart) {
  return multiPart ? `photos-${day}-part${partIndex}.zip` : `photos-${day}.zip`
}

// entries: [{ name, bytes: Uint8Array, mtime?: Date }]. Resolves a Blob of
// type application/zip. Store mode, so peak memory is about the part size.
export function buildZipBlob(entries) {
  return new Promise((resolve, reject) => {
    const chunks = []
    const zip = new Zip((err, chunk, final) => {
      if (err) { reject(err); return }
      chunks.push(chunk)
      if (final) resolve(new Blob(chunks, { type: 'application/zip' }))
    })
    for (const entry of entries) {
      const file = new ZipPassThrough(entry.name)
      if (entry.mtime instanceof Date && !isNaN(entry.mtime.getTime())) file.mtime = entry.mtime
      zip.add(file)
      file.push(entry.bytes, true)
    }
    zip.end()
  })
}
